import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage, TOKEN_STORAGE_KEY } from "../../api/client";
import * as authApi from "../../api/auth";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";
import { Logo } from "../../components/ui/Logo";

interface CambioPasswordForm {
  current_password: string;
  new_password: string;
  confirm: string;
}

/**
 * Cambio della password temporanea al primo accesso (o dopo un reset fatto da
 * chi gestisce l'account).
 *
 * Il vincolo vero sta nel backend: finché `must_change_password` è true ogni
 * API risponde 403, tranne `GET /auth/me` e `POST /auth/change-password`.
 * Questa pagina è solo il modo di uscirne — per questo è fuori da AppLayout,
 * che al montaggio chiamerebbe API che il backend rifiuta.
 *
 * Dopo il cambio il token con cui si è entrati non vale più (il backend sposta
 * `password_changed_at`): la risposta ne porta uno nuovo, che sostituisce il
 * vecchio prima di qualunque altra chiamata.
 */
export default function CambioPassword() {
  const { user, logout, setUser, setPermissions } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const minimo = user?.role === "admin" || user?.role === "staff" ? 12 : 8;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CambioPasswordForm>();

  const onSubmit = handleSubmit(async ({ current_password, new_password }) => {
    try {
      const { token } = await authApi.changePassword(current_password, new_password);
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      const { user: aggiornato, permissions } = await authApi.me();
      setPermissions(permissions);
      setUser(aggiornato);
      showToast("Password cambiata.", "success");
      navigate("/", { replace: true });
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile cambiare la password. Riprova."), "error");
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4">
      <div className="w-full max-w-sm rounded-card bg-white p-8 shadow-card">
        <div className="mb-8 text-center">
          <Logo className="mx-auto w-44" />
          <p className="mt-2 text-sm text-ink/50">
            {user?.must_change_password
              ? "Prima di continuare, sostituisci la password temporanea con una scelta da te."
              : "Scegli una nuova password"}
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <Input
            label={user?.must_change_password ? "Password temporanea" : "Password attuale"}
            type="password"
            autoComplete="current-password"
            required
            error={errors.current_password?.message}
            {...register("current_password", { required: "Inserisci la password attuale." })}
          />
          <Input
            label="Nuova password"
            type="password"
            autoComplete="new-password"
            required
            hint={`Almeno ${minimo} caratteri.`}
            error={errors.new_password?.message}
            {...register("new_password", {
              required: "La nuova password è obbligatoria.",
              minLength: { value: minimo, message: `Almeno ${minimo} caratteri.` },
              validate: (value) =>
                value !== watch("current_password") || "La nuova password deve essere diversa da quella attuale.",
            })}
          />
          <Input
            label="Conferma nuova password"
            type="password"
            autoComplete="new-password"
            required
            error={errors.confirm?.message}
            {...register("confirm", {
              required: "Conferma la nuova password.",
              validate: (value) => value === watch("new_password") || "Le due password non coincidono.",
            })}
          />
          <Button type="submit" loading={isSubmitting} className="mt-2 w-full">
            Cambia password
          </Button>
          <button
            type="button"
            onClick={() => {
              logout();
              navigate("/login", { replace: true });
            }}
            className="self-center text-xs font-semibold text-primary hover:underline"
          >
            Esci
          </button>
        </form>
      </div>
    </div>
  );
}
